// @ts-strict-ignore
import Icon, {
    CloudDownloadOutlined,
    CloudSyncOutlined,
    CloudUploadOutlined,
    DownOutlined,
    RightSquareOutlined,
    SaveOutlined,
    ThunderboltFilled,
    TranslationOutlined,
} from '@ant-design/icons';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Layout from 'antd/lib/layout';
import { Content, Footer } from 'antd/lib/layout/layout';
import Space from 'antd/lib/space';
import Tabs from 'antd/lib/tabs';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { compact } from 'lodash';
import * as React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
    BaseUpdateReleaseRequest,
    LocalizationUpdateRequest,
    tryUpdateRelease,
    UpdateMetadataApprovalRequestReleaseStep,
    UpdateReleaseNotesRequestReleaseStep,
} from '../actions/release';
import { ReleaseUpdatable } from '../actions/updatable';
import { getPreviousReleaseNotes } from '../api';
import {
    getAutomationIdByType,
    getError,
    getIsLoading,
    getUpdated,
    isMetadataFieldPendingTranslationStatus,
    shouldDisableMetadataScreenshotsEditing,
    translationIntegrationHasMetadataProject,
} from '../helpers';
import { RunwayUserAction } from '../helpers/aclHelpers';
import {
    displayConsoleNameForAppStoreType,
    displayConsoleNameWithPrefix,
    metadataRequirementsLinkForPlatform,
    selectedMetadataProjectNameForTranslationsIntegration,
    selectedMetadataProjectURLForTranslationsIntegration,
    statusColorForLocalizationStatus,
} from '../helpers/displayHelpers';
import { RootState } from '../reducers/reducers';
import {
    selectAppPlatform,
    selectAppStoreType,
    selectAuthCredentials,
    selectAutomationById,
    selectDefaultReleaseNotes,
    selectRelease,
    selectReleaseStep,
    selectStepRun,
    selectTranslationsIntegration,
    selectIsWorkflowsMigratedAndEnabled,
    selectWorkflowEnabledSteps,
} from '../selectors/selectors';
import {
    AppStoreType,
    AutomationSyncMetadataTranslations,
    AutomationType,
    IntegrationType,
    LocalizationStatus,
    LocalizedMetadata,
    MetadataTranslationLocale,
    MetadataTranslationStatus,
    NonErrorRunwayResponseTypes,
    ReleaseStepMetadataData,
    ReleaseStepStatus,
    RunwayResponseType,
    ReleaseStepType,
} from '../types';
import { ReleaseComponentProps, StringKeyedDictionary } from '../typescriptHelpers';
import { globalMessage } from '../utils/globalMessage';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import SmallStatusNode from './SmallStatusNode';
import CompletedTasksComponent from './metadata/CompletedTasksComponent';
import { ReleaseEventsTimelineSection } from './releaseOverview/ReleaseTimelineSection';
import { antGrey, runwayColors, tagStyle } from './styles/globalStyles';
import styles from './styles/stepStyles';
import { ReleaseTimelineSectionContext } from './timelineEvents/EventsTimeline';

import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import { createAppSettingsAutomationsRouteWithAutomation } from '@/helpers/routeHelpers';
import { ExtendedFormObject } from '@/utils/ExtendedForm';
import {
    useError,
    useIsLoading,
    useUpdated,
    useCallUpdatable,
    useCallUpdatableForCollections,
} from '@/utils/hooks/helpers';
import { useMultiExtendedForm } from '@/utils/hooks/useMultiExtendedForm';
import { Tag } from 'antd/lib';
import Link from 'antd/lib/typography/Link';
import { Link as RouterLink } from 'react-router-dom';
import { MetadataFormValues, StepContentMetadataForm } from './StepContentMetadataForm';
import { StepRunUpdatable } from '../actions/stepRun';
import { DefaultMetadataLocale, MetadataStepSettings } from '@/types/stepConfig';
import { ApprovalButtons, AutoApproveEnabledButton } from './ApprovalButtons';

interface MetadataTranslationsProviderComponentProps extends ReleaseComponentProps {
    uploadNeededCount: number;
    exportNeededCount: number;
    autoSyncMetadataTranslationsEnabled: boolean;
    autoSyncMetadataTranslationsName: string;
    appStoreType: AppStoreType;
    selectedProjectName?: string;
    selectedProjectURL?: string;
    integrationId?: IntegrationType;
    integrationName?: string;
}

export const MetadataTranslationsProviderComponent: React.FC<MetadataTranslationsProviderComponentProps> = (props) => {
    const dispatch = useDispatch();

    const onClick = (releaseUpdatable: ReleaseUpdatable) => {
        const request: BaseUpdateReleaseRequest = {
            appId: props.appId,
            releaseId: props.releaseId,
            orgId: props.orgId,
        };
        dispatch(tryUpdateRelease(releaseUpdatable, request, props.releaseId));
    };

    const onClickSyncTranslations = () => {
        onClick(ReleaseUpdatable.SyncMetadataTranslations);
    };

    const onClickUploadTranslations = () => {
        onClick(ReleaseUpdatable.UploadMetadataForTranslation);
    };

    const onClickExportTranslations = () => {
        onClick(ReleaseUpdatable.ExportMetadataTranslations);
    };

    const renderSyncInfo = () => {
        let tagText = '';
        let tagColor = '';

        const isSynced = props.exportNeededCount === 0 && props.uploadNeededCount === 0;
        if (isSynced) {
            tagText = 'synced';
            tagColor = runwayColors.statusGreen;
        } else {
            tagText = 'not synced';
            tagColor = runwayColors.statusYellow;
        }

        return (
            <Tooltip
                title={
                    isSynced ? null : (
                        <div style={{ color: runwayColors.white }}>
                            <style>{`
                                ul > li::before {
                                content: "";
                                margin-left: -0.5rem;
                                }
                            `}</style>
                            <ul style={{ listStylePosition: 'inside', paddingLeft: 0 }}>
                                {props.exportNeededCount > 0 && (
                                    <li>
                                        <Text strong style={{ color: runwayColors.white }}>
                                            Export needed
                                        </Text>{' '}
                                        - {props.exportNeededCount} new{' '}
                                        {props.exportNeededCount != 1 ? 'translations are ' : 'translation is '}
                                        available to export
                                    </li>
                                )}
                                {props.uploadNeededCount > 0 && (
                                    <li>
                                        <Text strong style={{ color: runwayColors.white }}>
                                            Upload needed
                                        </Text>{' '}
                                        - {props.uploadNeededCount}{' '}
                                        {props.uploadNeededCount != 1 ? 'changes' : 'change'} found to metadata in your
                                        source language
                                    </li>
                                )}
                            </ul>
                            <Text style={{ color: runwayColors.white }}>
                                Use the &apos;Sync&apos; action to upload updated metadata for translation and export
                                new translations to {displayConsoleNameForAppStoreType(props.appStoreType)}.
                            </Text>
                        </div>
                    )
                }
            >
                <Tag style={{ ...tagStyle, color: tagColor, borderColor: tagColor }} color={runwayColors.white}>
                    {tagText}
                </Tag>
            </Tooltip>
        );
    };

    // TODO: #WORKFLOWS# Use automation id instead of hardcoded type
    const automationsRoute = createAppSettingsAutomationsRouteWithAutomation(
        props.orgId,
        props.appId,
        AutomationType.AutoSyncMetadataTranslations,
    );

    const syncTranslationsIsLoading = useIsLoading(ReleaseUpdatable.SyncMetadataTranslations, props.releaseId);
    const syncTranslationsError = useError(ReleaseUpdatable.SyncMetadataTranslations, props.releaseId);
    const syncTranslationsUpdated = useUpdated(ReleaseUpdatable.SyncMetadataTranslations, props.releaseId);

    const uploadTranslationsIsLoading = useIsLoading(ReleaseUpdatable.UploadMetadataForTranslation, props.releaseId);
    const uploadTranslationsError = useError(ReleaseUpdatable.UploadMetadataForTranslation, props.releaseId);
    const uploadTranslationsUpdated = useUpdated(ReleaseUpdatable.UploadMetadataForTranslation, props.releaseId);

    const exportTranslationsIsLoading = useIsLoading(ReleaseUpdatable.ExportMetadataTranslations, props.releaseId);
    const exportTranslationsError = useError(ReleaseUpdatable.ExportMetadataTranslations, props.releaseId);
    const exportTranslationsUpdated = useUpdated(ReleaseUpdatable.ExportMetadataTranslations, props.releaseId);

    React.useEffect(() => {
        if (uploadTranslationsError) {
            globalMessage.error(uploadTranslationsError);
        }
    }, [uploadTranslationsError]);

    React.useEffect(() => {
        if (uploadTranslationsUpdated) {
            globalMessage.success('Metadata uploaded for translations successfully');
        }
    }, [uploadTranslationsUpdated]);

    React.useEffect(() => {
        if (syncTranslationsError) {
            globalMessage.error(syncTranslationsError);
        }
    }, [syncTranslationsError]);

    React.useEffect(() => {
        if (syncTranslationsUpdated) {
            globalMessage.success('Sync has been triggered! Check back here for updated status.');
        }
    }, [syncTranslationsUpdated]);

    React.useEffect(() => {
        if (exportTranslationsError) {
            globalMessage.error(exportTranslationsError);
        }
    }, [exportTranslationsError]);

    React.useEffect(() => {
        if (exportTranslationsUpdated) {
            globalMessage.success(
                `Export to ${displayConsoleNameWithPrefix(
                    props.appStoreType,
                )} has been triggered! Check back here for updated status.`,
            );
        }
    }, [exportTranslationsUpdated]);

    const isLoading = syncTranslationsIsLoading || uploadTranslationsIsLoading || exportTranslationsIsLoading;

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 8,
            }}
        >
            <Space>
                <Icon style={{ fontSize: '16px', color: antGrey.grey7 }} component={TranslationOutlined} />
                <Text style={{ ...tagStyle, color: antGrey.grey7, fontWeight: 400 }}>
                    {props.integrationName ?? 'Translations'} / Translations status
                </Text>
            </Space>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: antGrey.grey2,
                    border: '1px solid',
                    borderRadius: '8px',
                    borderColor: antGrey.grey4,
                    padding: '16px 12px',
                }}
            >
                <Space size={16}>
                    {props.integrationId && <IntegrationIcon integrationId={props.integrationId} size={24} />}
                    <Link style={{ fontSize: '16px', fontWeight: 600 }} href={props.selectedProjectURL} target="_blank">
                        {props.selectedProjectName ?? 'Translations project'}
                    </Link>
                </Space>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    {props.autoSyncMetadataTranslationsEnabled && (
                        <Tooltip
                            title={
                                <div>
                                    <div>
                                        {props.autoSyncMetadataTranslationsName} automation is <strong>enabled</strong>.
                                    </div>
                                    <div>
                                        <RouterLink
                                            style={{ color: runwayColors.white, textDecoration: 'underline' }}
                                            to={automationsRoute}
                                        >
                                            View settings
                                        </RouterLink>
                                    </div>
                                </div>
                            }
                        >
                            <ThunderboltFilled style={{ color: runwayColors.primary, fontSize: 18, marginRight: 6 }} />
                        </Tooltip>
                    )}
                    {renderSyncInfo()}
                    <PermissionedActionButton
                        actionType={RunwayUserAction.UAUploadExportMetadataTranslations}
                        menu={{
                            items: [
                                {
                                    key: 'upload',
                                    label: `Upload metadata${
                                        props.integrationName ? ` to ${props.integrationName}` : ' '
                                    } for translation`,
                                    icon: <CloudUploadOutlined />,
                                    disabled: props.uploadNeededCount == 0 || isLoading,
                                    onClick: () => onClickUploadTranslations(),
                                },
                                {
                                    key: 'export',
                                    label: `Export translations${
                                        props.integrationName ? ` from ${props.integrationName}` : ''
                                    }`,
                                    icon: <CloudDownloadOutlined />,
                                    disabled: props.exportNeededCount == 0 || isLoading,
                                    onClick: () => onClickExportTranslations(),
                                },
                            ],
                        }}
                        buttonsRender={([leftButton, rightButton]) => [
                            React.cloneElement(leftButton as React.ReactElement<any, string>, {
                                loading: isLoading,
                            }),

                            React.cloneElement(rightButton as React.ReactElement<any, string>, {
                                loading: isLoading,
                            }),
                        ]}
                        trigger={['click']}
                        loading={isLoading}
                        onClick={() => onClickSyncTranslations()}
                        icon={<CloudSyncOutlined />}
                        type="primary"
                        disabled={props.exportNeededCount === 0 && props.uploadNeededCount === 0}
                    >
                        Sync
                    </PermissionedActionButton>
                </div>
            </div>
        </div>
    );
};

const renderLocaleStatusNode = (
    status: LocalizationStatus,
    displayLocale: string,
    appStoreVersionExists: boolean,
    translationsLocalizationName: string,
    appStoreType: AppStoreType,
    localizationsForSourceLocale?: LocalizedMetadata,
    translationStatus?: MetadataTranslationLocale,
) => {
    switch (status) {
        case LocalizationStatus.Ready:
            return (
                <SmallStatusNode
                    color={statusColorForLocalizationStatus(status)}
                    tooltip={
                        <Text style={{ color: runwayColors.white }}>
                            <Text strong style={{ color: runwayColors.white }}>
                                Ready
                            </Text>{' '}
                            {appStoreVersionExists ?
                                `- all required metadata fields are present and synced in ${displayConsoleNameForAppStoreType(appStoreType)}.`
                            :   '- all required metadata fields are present and the latest translations have been saved to Runway.'
                            }
                        </Text>
                    }
                />
            );
        case LocalizationStatus.Pending:
            return (
                <SmallStatusNode
                    color={statusColorForLocalizationStatus(status)}
                    tooltip={
                        <Text style={{ color: runwayColors.white }}>
                            <Text strong style={{ color: runwayColors.white }}>
                                Missing metadata
                            </Text>{' '}
                            - one or more metadata fields is required but not present for the {displayLocale} locale.
                        </Text>
                    }
                />
            );
        case LocalizationStatus.PendingTranslations: {
            const waitingForTranslation = isMetadataFieldPendingTranslationStatus(
                MetadataTranslationStatus.TranslationsPending,
                translationStatus,
                localizationsForSourceLocale,
            );
            const exportNeeded = isMetadataFieldPendingTranslationStatus(
                MetadataTranslationStatus.ExportNeeded,
                translationStatus,
                localizationsForSourceLocale,
            );
            const localeNotFound = isMetadataFieldPendingTranslationStatus(
                MetadataTranslationStatus.NotFound,
                translationStatus,
                localizationsForSourceLocale,
            );
            return (
                <SmallStatusNode
                    color={statusColorForLocalizationStatus(status)}
                    isOpenStatusNode={true}
                    tooltip={
                        <div>
                            {waitingForTranslation && (
                                <div style={{ marginBottom: exportNeeded || localeNotFound ? 8 : 0 }}>
                                    <Text style={{ color: runwayColors.white }}>
                                        <Text strong style={{ color: runwayColors.white }}>
                                            Waiting for translation
                                        </Text>{' '}
                                        - one or more metadata fields in this locale are awaiting translation in{' '}
                                        {translationsLocalizationName}.
                                    </Text>
                                </div>
                            )}
                            {exportNeeded && (
                                <div style={{ marginBottom: localeNotFound ? 8 : 0 }}>
                                    <Text style={{ color: runwayColors.white }}>
                                        <Text strong style={{ color: runwayColors.white }}>
                                            Export needed
                                        </Text>{' '}
                                        - one or more metadata fields in this locale are ready to export from{' '}
                                        {translationsLocalizationName} to{' '}
                                        {displayConsoleNameForAppStoreType(appStoreType)}. Click &apos;Sync&apos; to
                                        export all translated metadata.
                                    </Text>
                                </div>
                            )}
                            {localeNotFound && (
                                <div>
                                    <Text style={{ color: runwayColors.white }}>
                                        <Text strong style={{ color: runwayColors.white }}>
                                            Locale not found
                                        </Text>{' '}
                                        - translations for this locale were not found in {translationsLocalizationName}.
                                    </Text>
                                </div>
                            )}
                        </div>
                    }
                />
            );
        }
        default:
            return <SmallStatusNode color={statusColorForLocalizationStatus(status)} />;
    }
};

export const StepContentMetadata: React.FC<ReleaseStepOrStepRunComponentProps> = ({
    stepRunComponentProps,
    orgId,
    appId,
    releaseId,
    releaseStepType,
}) => {
    const dispatch = useDispatch();

    // Replace all selector calls with useSelector hooks
    const release = useSelector((state: RootState) => selectRelease(state, releaseId));
    const releaseStep = useSelector((state: RootState) => selectReleaseStep(state, releaseId, releaseStepType));
    const userId = useSelector((state: RootState) => state.user.id);
    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));
    const translationsIntegration = useSelector((state: RootState) => selectTranslationsIntegration(state, appId));

    const stepRunId = stepRunComponentProps?.stepRunId;
    const stepRun = useSelector((state: RootState) => selectStepRun(state, stepRunId));
    const stepData: ReleaseStepMetadataData =
        stepRun ? { ...stepRun.data.summaryData, ...stepRun.data.complementaryData } : releaseStep.data;
    const requiresManualApproval = stepData?.requiresManualApproval ?? true;

    const automationRuns = stepRun?.automationRuns ?? releaseStep?.automationRuns;

    const platform = useSelector((state: RootState) => {
        return stepRun ? stepData.platform : selectAppPlatform(state, appId);
    });
    const appStoreType = useSelector((state: RootState) => {
        return stepRun ? stepData.appStoreType : selectAppStoreType(state, appId);
    });

    const betaTesterNotes = stepData.selectedBetaBuild?.betaTestingNotes;
    const metadataURLString = stepData.storeUrl;
    const isApproved = stepData.isApproved;
    const primaryLocale = stepData.primaryLocale;

    const localizations = stepData.localizations;

    const appStoreDefaultReleaseNotes: DefaultMetadataLocale[] = useSelector((state: RootState) => {
        if (stepRun) {
            const metadataStepSettings = stepRun.stepConfigSnapshot.settings as MetadataStepSettings;
            return metadataStepSettings?.defaultMetadata ?? [];
        }

        return selectDefaultReleaseNotes(state, appId) as DefaultMetadataLocale[];
    });

    const shouldDisableMetadataEditing = shouldDisableMetadataScreenshotsEditing(
        appStoreType,
        release.status,
        release.type,
    );

    const disableApproveButton =
        stepRun ? stepRun.status === ReleaseStepStatus.Blocked : releaseStep.status === ReleaseStepStatus.Blocked;

    const defaultReleaseNotesByLocale: Record<string, string> = React.useMemo(() => {
        return appStoreDefaultReleaseNotes.reduce(
            (acc, releaseNotes) => {
                const id = localizations.find((localization) => localization.locale === releaseNotes.locale)?.id;
                if (id) {
                    acc[id] = releaseNotes.whatsNew;
                }
                return acc;
            },
            {} as Record<string, string>,
        );
    }, [appStoreDefaultReleaseNotes, localizations]);

    const betaTesterNotesByLocale: Record<string, string> = React.useMemo(() => {
        return (
            betaTesterNotes?.reduce(
                (acc, releaseNotes) => {
                    const id = localizations.find((localization) => localization.locale === releaseNotes.locale)?.id;
                    if (id) {
                        acc[id] = releaseNotes.notes;
                    }
                    return acc;
                },
                {} as Record<string, string>,
            ) ?? {}
        );
    }, [betaTesterNotes, localizations]);

    const updatedById: StringKeyedDictionary<boolean> = React.useMemo(() => ({}), []);
    const errorById: StringKeyedDictionary<string> = React.useMemo(() => ({}), []);
    const loadingById: StringKeyedDictionary<boolean> = React.useMemo(() => ({}), []);

    const releaseNotesStates = useSelector((state: RootState) => {
        localizations.forEach((localization) => {
            updatedById[localization.id] = getUpdated(ReleaseUpdatable.UpdateReleaseNotes, state, localization.id);
            loadingById[localization.id] = getIsLoading(ReleaseUpdatable.UpdateReleaseNotes, state, localization.id);
            errorById[localization.id] = getError(ReleaseUpdatable.UpdateReleaseNotes, state, localization.id);
        });

        return {
            releaseNotesUpdatedById: updatedById,
            releaseNotesErrorById: errorById,
            releaseNotesLoadingById: loadingById,
        };
    }, shallowEqual);

    const {
        releaseNotesUpdatedById: releaseNotesUpdatedByIdReleaseStep,
        releaseNotesErrorById: releaseNotesErrorByIdReleaseStep,
        releaseNotesLoadingById: releaseNotesLoadingByIdReleaseStep,
    } = releaseNotesStates;

    const [
        updateMetadataForAllLocales,
        updateMetadataForAllLocalesIsLoading,
        updateMetadataForAllLocalesResponseType,
        updateMetadataForAllLocalesError,
    ] = useCallUpdatable(StepRunUpdatable.UpdateMetadata, stepRunComponentProps);

    const [
        updateMetadataForSpecificLocaleCollection,
        updateMetadataForSpecificLocaleIsLoadingCollection,
        updateMetadataForSpecificLocaleResponseTypeCollection,
        updateMetadataForSpecificLocaleErrorCollection,
    ] = useCallUpdatableForCollections(
        StepRunUpdatable.UpdateMetadata,
        localizations.reduce((acc, localization) => {
            acc[localization.id] = stepRunComponentProps;
            return acc;
        }, {}),
        localizations.map((localization) => localization.id),
    );

    const releaseNotesLoadingById =
        stepRunComponentProps ? updateMetadataForSpecificLocaleIsLoadingCollection : releaseNotesLoadingByIdReleaseStep;

    const releaseNotesErrorById =
        stepRunComponentProps ? updateMetadataForSpecificLocaleErrorCollection : releaseNotesErrorByIdReleaseStep;

    const releaseNotesUpdatedById =
        stepRunComponentProps ?
            updateMetadataForSpecificLocaleResponseTypeCollection
        :   releaseNotesUpdatedByIdReleaseStep;

    const anyLocalizationIsLoading = Object.values(releaseNotesLoadingById).includes(true);

    const allReleaseNotesUpdatedReleaseStep = useSelector((state: RootState) =>
        getUpdated(ReleaseUpdatable.UpdateReleaseNotes, state, releaseId),
    );
    const allReleaseNotesUpdateIsLoadingReleaseStep = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.UpdateReleaseNotes, state, releaseId),
    );
    const allReleaseNotesUpdateErrorReleaseStep = useSelector((state: RootState) =>
        getError(ReleaseUpdatable.UpdateReleaseNotes, state, releaseId),
    );

    const allReleaseNotesUpdated =
        stepRunComponentProps ?
            updateMetadataForAllLocalesResponseType === RunwayResponseType.Success
        :   allReleaseNotesUpdatedReleaseStep;
    const allReleaseNotesUpdateIsLoading =
        stepRunComponentProps ? updateMetadataForAllLocalesIsLoading : allReleaseNotesUpdateIsLoadingReleaseStep;
    const allReleaseNotesUpdateError =
        stepRunComponentProps ? updateMetadataForAllLocalesError : allReleaseNotesUpdateErrorReleaseStep;

    React.useEffect(() => {
        if (allReleaseNotesUpdated) {
            globalMessage.success('Release notes updated successfully.');
        }
    }, [allReleaseNotesUpdated]);

    React.useEffect(() => {
        if (allReleaseNotesUpdateError) {
            globalMessage.error(allReleaseNotesUpdateError);
        }
    }, [allReleaseNotesUpdateError]);

    const isAnyUpdated = Object.values(releaseNotesUpdatedById).some((updated) => updated);
    React.useEffect(() => {
        for (const [id, updated] of Object.entries<boolean | NonErrorRunwayResponseTypes>(releaseNotesUpdatedById)) {
            const displayLocale = localizations.find((localization) => localization.id === id)?.displayLocale;
            if (updated === true || updated === RunwayResponseType.Success) {
                globalMessage.success(`Release notes for ${displayLocale} updated successfully.`);
                extendedFormObjects[id]?.updateState?.({
                    isDirty: false,
                });
            }
        }
    }, [isAnyUpdated]);

    const isAnyError = Object.values(releaseNotesErrorById).some((error) => error);
    React.useEffect(() => {
        for (const [_, error] of Object.entries(releaseNotesErrorById)) {
            if (error) {
                globalMessage.error(String(error));
            }
        }
    }, [isAnyError]);

    const [
        approveMetadataStepRun,
        approveMetadataIsLoadingStepRun,
        approveMetadataResponseTypeStepRun,
        approveMetadataErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.ApproveMetadata, stepRunComponentProps);

    const [
        updateApprovalOverride,
        updateApprovalOverrideIsLoading,
        updateApprovalOverrideResponseType,
        updateApprovalOverrideError,
    ] = useCallUpdatable(StepRunUpdatable.UpdateApprovalOverride, stepRunComponentProps);

    React.useEffect(() => {
        if (updateApprovalOverrideResponseType === RunwayResponseType.Success) {
            globalMessage.success('Approval setting updated.');
        }
    }, [updateApprovalOverrideResponseType]);

    React.useEffect(() => {
        if (updateApprovalOverrideError) {
            globalMessage.error(updateApprovalOverrideError);
        }
    }, [updateApprovalOverrideError]);

    const metadataApprovedReleaseStep = useSelector((state: RootState) =>
        getUpdated(ReleaseUpdatable.MetadataApproval, state, releaseId),
    );
    const metadataApprovalIsLoadingReleaseStep = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.MetadataApproval, state, releaseId),
    );
    const metadataApprovalErrorReleaseStep = useSelector((state: RootState) =>
        getError(ReleaseUpdatable.MetadataApproval, state, releaseId),
    );

    const metadataApprovalIsLoading =
        stepRunComponentProps ? approveMetadataIsLoadingStepRun : metadataApprovalIsLoadingReleaseStep;
    const metadataApproved =
        stepRunComponentProps ?
            approveMetadataResponseTypeStepRun === RunwayResponseType.Success
        :   metadataApprovedReleaseStep;
    const metadataApprovalError =
        stepRunComponentProps ? approveMetadataErrorStepRun : metadataApprovalErrorReleaseStep;

    React.useEffect(() => {
        if (metadataApproved) {
            globalMessage.success('Metadata approval updated successfully.');
        }
    }, [metadataApproved]);

    React.useEffect(() => {
        if (metadataApprovalError) {
            globalMessage.error(metadataApprovalError);
        }
    }, [metadataApprovalError]);

    const translationsSourceLocale = stepData.translationsSourceLocale;
    const translationStatuses = stepData.translationStatuses;
    const localizationsForSourceLocale = localizations.find(
        (localization) => localization.locale === translationsSourceLocale,
    );
    const appStoreVersionExists = stepData.appStoreVersionExists;

    const statusAccumulator = function (
        totalCount: number,
        metadataTranslationLocale: MetadataTranslationLocale,
        metadataStatus: MetadataTranslationStatus,
    ) {
        let count = 0;
        if (metadataTranslationLocale.description === metadataStatus) {
            count++;
        }
        if (metadataTranslationLocale.appName === metadataStatus) {
            count++;
        }
        if (metadataTranslationLocale.subtitle === metadataStatus) {
            count++;
        }
        if (metadataTranslationLocale.keywords === metadataStatus) {
            count++;
        }
        if (metadataTranslationLocale.promotionalText === metadataStatus) {
            count++;
        }
        if (metadataTranslationLocale.shortDescription === metadataStatus) {
            count++;
        }
        if (metadataTranslationLocale.whatsNew === metadataStatus) {
            count++;
        }
        return totalCount + count;
    };

    const uploadNeededCount =
        translationStatuses?.reduce((totalCount, metadataTranslationLocale) => {
            return statusAccumulator(totalCount, metadataTranslationLocale, MetadataTranslationStatus.UploadNeeded);
        }, 0) ?? 0;

    const exportNeededCount =
        translationStatuses?.reduce((totalCount, metadataTranslationLocale) => {
            return statusAccumulator(totalCount, metadataTranslationLocale, MetadataTranslationStatus.ExportNeeded);
        }, 0) ?? 0;

    const syncMetadataTranslationsAutomation = useSelector((state: RootState) =>
        selectAutomationById(
            state,
            appId,
            getAutomationIdByType(automationRuns, AutomationType.AutoSyncMetadataTranslations),
        ),
    ) as AutomationSyncMetadataTranslations;

    const shouldShowTranslationsProviderStatus =
        !!translationsIntegration && translationIntegrationHasMetadataProject(translationsIntegration);

    const [featuresListDrawerVisible, setFeaturesListDrawerVisible] = React.useState(false);
    const [isLoadingGetPreviousReleaseNotes, setIsLoadingGetPreviousReleaseNotes] = React.useState(false);
    const [isLoadingGetPreviousReleaseNotesForSpecificId, setIsLoadingGetPreviousReleaseNotesForSpecificId] =
        React.useState(false);

    const [extendedFormStates, extendedFormObjects] = useMultiExtendedForm<MetadataFormValues>({
        ids: localizations.map((localization) => localization.id),
    });

    const areAllFormsPristine = Object.values(extendedFormStates).every((state) => !state.isDirty);
    const anyFormIsInvalid = Object.values(extendedFormStates).some((state) => state.isInvalid);
    const hasUnsavedMetadataChanges = !areAllFormsPristine;
    const [pendingApproveAfterSave, setPendingApproveAfterSave] = React.useState(false);
    const [pendingApproveSaveStarted, setPendingApproveSaveStarted] = React.useState(false);

    const buildUpdateMetadataRequest = React.useCallback(
        (specificId?: string): UpdateReleaseNotesRequestReleaseStep => {
            const updateRequests: LocalizationUpdateRequest[] = Object.entries(extendedFormObjects)
                .filter(([id]) => {
                    if (specificId) {
                        return id === specificId;
                    }
                    return true;
                })
                .map(([id, extendedFormObject]) => {
                    const values = extendedFormObject.form?.getFieldsValue();
                    const locale = localizations.find((localization) => localization.id === id)?.locale;
                    const updateRequest: LocalizationUpdateRequest = {
                        locale,
                        whatsNew: values.whatsNew,
                        keywords: values.keywords,
                        shortDescription: values.shortDescription,
                        promotionalText: values.promotionalText,
                        description: values.description,
                        subtitle: values.subtitle,
                        videoUrl: values.videoUrl,
                        appName: values.appName,
                    };
                    return updateRequest;
                });

            return {
                orgId,
                releaseId,
                appId,
                localizations: compact(updateRequests),
            };
        },
        [extendedFormObjects, localizations, orgId, releaseId, appId],
    );

    const onClickSaveAllLocalizations = React.useCallback(
        (specificId?: string) => {
            const request = buildUpdateMetadataRequest(specificId);
            if (stepRunComponentProps) {
                if (specificId) {
                    updateMetadataForSpecificLocaleCollection[specificId](request);
                } else {
                    updateMetadataForAllLocales(request);
                }
            } else {
                dispatch(tryUpdateRelease(ReleaseUpdatable.UpdateReleaseNotes, request, specificId ?? releaseId));
            }
        },
        [
            stepRunComponentProps,
            updateMetadataForSpecificLocaleCollection,
            updateMetadataForAllLocales,
            buildUpdateMetadataRequest,
            releaseId,
            dispatch,
        ],
    );

    const onClickApproveMetadata = React.useCallback(
        (isApproved: boolean) => {
            const request: UpdateMetadataApprovalRequestReleaseStep = {
                userId,
                orgId,
                releaseId,
                appId,
                isApproved,
            };
            if (stepRunComponentProps) {
                approveMetadataStepRun(request);
            } else {
                dispatch(tryUpdateRelease(ReleaseUpdatable.MetadataApproval, request, releaseId));
            }
        },
        [stepRunComponentProps, approveMetadataStepRun, userId, orgId, releaseId, appId, dispatch],
    );

    React.useEffect(() => {
        if (pendingApproveAfterSave && allReleaseNotesUpdateIsLoading) {
            setPendingApproveSaveStarted(true);
        }
    }, [pendingApproveAfterSave, allReleaseNotesUpdateIsLoading]);

    const onClickApproveMetadataRef = React.useRef(onClickApproveMetadata);
    onClickApproveMetadataRef.current = onClickApproveMetadata;
    const hasFiredApproveAfterSaveRef = React.useRef(false);

    React.useEffect(() => {
        if (!pendingApproveAfterSave || !pendingApproveSaveStarted || !allReleaseNotesUpdated) {
            return;
        }
        if (hasFiredApproveAfterSaveRef.current) {
            return;
        }

        hasFiredApproveAfterSaveRef.current = true;
        setPendingApproveAfterSave(false);
        setPendingApproveSaveStarted(false);
        onClickApproveMetadataRef.current(true);
    }, [pendingApproveAfterSave, pendingApproveSaveStarted, allReleaseNotesUpdated]);

    React.useEffect(() => {
        if (!pendingApproveAfterSave || !allReleaseNotesUpdateError) {
            return;
        }

        setPendingApproveAfterSave(false);
        setPendingApproveSaveStarted(false);
    }, [pendingApproveAfterSave, allReleaseNotesUpdateError]);

    const onClickSetDefaultReleaseNotes = React.useCallback(
        (specificId?: string) => {
            Object.entries(extendedFormObjects)
                .filter(([id]) => {
                    if (specificId) {
                        return id === specificId;
                    }
                    return true;
                })
                .forEach(([id, extendedFormObject]) => {
                    if (defaultReleaseNotesByLocale?.[id]) {
                        extendedFormObject.form?.setFieldsValue({
                            whatsNew: defaultReleaseNotesByLocale[id],
                        });
                        extendedFormObject.resetDirty?.();
                    }
                });

            globalMessage.success('Default release notes set successfully.');
        },
        [extendedFormObjects, defaultReleaseNotesByLocale],
    );

    const onClickSetPreviousReleaseNotes = React.useCallback(
        async (specificId?: string) => {
            if (specificId) {
                setIsLoadingGetPreviousReleaseNotesForSpecificId(true);
            } else {
                setIsLoadingGetPreviousReleaseNotes(true);
            }
            try {
                const previousReleaseNotes = await getPreviousReleaseNotes(
                    { orgId, appId, releaseId },
                    authCredentials,
                );

                const previousReleaseNotesByLocale =
                    previousReleaseNotes?.reduce<Record<string, string>>((acc, releaseNote) => {
                        if (!releaseNote.locale) {
                            return acc;
                        }

                        if (releaseNote.whatsNew || !acc[releaseNote.locale]) {
                            acc[releaseNote.locale] = releaseNote.whatsNew;
                        }

                        return acc;
                    }, {}) ?? {};

                let populatedLocaleCount = 0;
                Object.entries(extendedFormObjects)
                    .filter(([id]) => {
                        if (specificId) {
                            return id === specificId;
                        }
                        return true;
                    })
                    .forEach(([id, extendedFormObject]) => {
                        const localeForId = localizations.find((localization) => localization.id === id)?.locale;
                        const previousNotes = localeForId ? previousReleaseNotesByLocale[localeForId] : undefined;
                        if (previousNotes) {
                            extendedFormObject.form?.setFieldsValue({ whatsNew: previousNotes });
                            extendedFormObject.resetDirty?.();
                            populatedLocaleCount += 1;
                        }
                    });

                if (populatedLocaleCount > 0) {
                    globalMessage.success('Previous release notes set successfully.');
                } else {
                    globalMessage.error('No previous release notes found for the selected locale.');
                }
            } catch (error) {
                globalMessage.error(`Error fetching previous release notes: ${error}`);
            } finally {
                if (specificId) {
                    setIsLoadingGetPreviousReleaseNotesForSpecificId(false);
                } else {
                    setIsLoadingGetPreviousReleaseNotes(false);
                }
            }
        },
        [
            extendedFormObjects,
            localizations,
            orgId,
            appId,
            releaseId,
            authCredentials,
            setIsLoadingGetPreviousReleaseNotesForSpecificId,
            setIsLoadingGetPreviousReleaseNotes,
        ],
    );

    const onClickSetBetaTesterNotes = React.useCallback(
        (specificId?: string) => {
            Object.entries(extendedFormObjects)
                .filter(([id]) => {
                    if (specificId) {
                        return id === specificId;
                    }
                    return true;
                })
                .forEach(([id, extendedFormObject]) => {
                    const value = betaTesterNotesByLocale?.[id];
                    if (value) {
                        extendedFormObject.form?.setFieldsValue({
                            whatsNew: value,
                        });
                        extendedFormObject.resetDirty?.();
                    }
                });

            globalMessage.success('Beta tester notes set successfully.');
        },
        [extendedFormObjects, betaTesterNotesByLocale],
    );

    const onApplyFieldToAllLocales = React.useCallback(
        (id: string, displayLocale: string, field: keyof MetadataFormValues) => {
            const value = extendedFormObjects[id]?.form?.getFieldValue(field);

            if (!value) {
                return;
            }

            Object.values(extendedFormObjects).forEach((extendedFormObject) => {
                extendedFormObject.form?.setFieldValue(field, value);
                extendedFormObject.resetDirty?.();
            });

            globalMessage.success(`Successfully applied field from ${displayLocale} to all locales!`);
        },
        [extendedFormObjects],
    );

    const onClickShowFeaturesList = React.useCallback(() => {
        setFeaturesListDrawerVisible(true);
    }, [setFeaturesListDrawerVisible]);

    const onClickSaveAllLocalizationsCallback = React.useCallback(() => {
        onClickSaveAllLocalizations();
    }, [onClickSaveAllLocalizations]);

    const onClickApproveMetadataCallback = React.useCallback(() => {
        if (isApproved) {
            onClickApproveMetadata(false);
            return;
        }

        if (hasUnsavedMetadataChanges) {
            hasFiredApproveAfterSaveRef.current = false;
            setPendingApproveAfterSave(true);
            setPendingApproveSaveStarted(false);
            onClickSaveAllLocalizations();
            return;
        }

        onClickApproveMetadata(true);
    }, [onClickApproveMetadata, onClickSaveAllLocalizations, isApproved, hasUnsavedMetadataChanges]);

    const onClickSetDefaultReleaseNotesCallback = React.useCallback(() => {
        onClickSetDefaultReleaseNotes();
    }, [onClickSetDefaultReleaseNotes]);

    const onClickSetPreviousReleaseNotesCallback = React.useCallback(() => {
        onClickSetPreviousReleaseNotes();
    }, [onClickSetPreviousReleaseNotes]);

    const onClickSetBetaTesterNotesCallback = React.useCallback(() => {
        onClickSetBetaTesterNotes();
    }, [onClickSetBetaTesterNotes]);

    const isWorkflowsMigratedAndEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsMigratedAndEnabled(state, appId),
    );
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));
    const isMetadataEnabled = !!workflowEnabledSteps[ReleaseStepType.Metadata];
    const shouldUseWFsMenuItems = isWorkflowsMigratedAndEnabled && isMetadataEnabled;

    const menuItems = React.useMemo(
        () => [
            {
                label: 'From default release notes',
                onClick: onClickSetDefaultReleaseNotesCallback,
                key: '1',
                disabled: Object.keys(defaultReleaseNotesByLocale).length === 0,
            },
            {
                label: 'From previous release',
                key: '2',
                onClick: onClickSetPreviousReleaseNotesCallback,
                disabled: isLoadingGetPreviousReleaseNotes,
            },
            {
                label: 'From latest beta tester notes',
                key: '3',
                onClick: onClickSetBetaTesterNotesCallback,
                disabled: Object.keys(betaTesterNotesByLocale).length === 0,
            },
        ],
        [
            onClickSetDefaultReleaseNotesCallback,
            onClickSetPreviousReleaseNotesCallback,
            onClickSetBetaTesterNotesCallback,
            defaultReleaseNotesByLocale,
            isLoadingGetPreviousReleaseNotes,
            betaTesterNotesByLocale,
        ],
    );

    const wfsMenuItems = React.useMemo(
        () => [
            {
                label: 'From default release notes',
                onClick: onClickSetDefaultReleaseNotesCallback,
                key: '1',
                disabled: Object.keys(defaultReleaseNotesByLocale).length === 0,
            },
            {
                label: 'From previous release',
                key: '2',
                onClick: onClickSetPreviousReleaseNotesCallback,
                disabled: isLoadingGetPreviousReleaseNotes,
            },
        ],
        [
            onClickSetDefaultReleaseNotesCallback,
            onClickSetPreviousReleaseNotesCallback,
            defaultReleaseNotesByLocale,
            isLoadingGetPreviousReleaseNotes,
        ],
    );

    const dropdownMenuItems = shouldUseWFsMenuItems ? wfsMenuItems : menuItems;

    const approveMetadataRequiresSave = !isApproved && hasUnsavedMetadataChanges;
    const approveMetadataLabel =
        approveMetadataRequiresSave ?
            appStoreVersionExists ? 'Save all changes and approve metadata'
            :   'Save all changes in Runway and approve metadata'
        :   undefined;
    const approveMetadataIsLoading =
        metadataApprovalIsLoading ||
        updateApprovalOverrideIsLoading ||
        (pendingApproveAfterSave && allReleaseNotesUpdateIsLoading);
    const disableApproveMetadataButton =
        disableApproveButton ||
        approveMetadataIsLoading ||
        (approveMetadataRequiresSave && (anyFormIsInvalid || anyLocalizationIsLoading || shouldDisableMetadataEditing));

    // Memoize translation statuses lookup to avoid recreating on every render
    const translationStatusByLocale = React.useMemo(() => {
        if (!translationStatuses) return {};
        return translationStatuses.reduce(
            (acc, status) => {
                acc[status.locale] = status;
                return acc;
            },
            {} as Record<string, MetadataTranslationLocale>,
        );
    }, [translationStatuses]);

    return (
        <Layout>
            <Content
                style={{
                    ...styles.innerContent,
                    ...styles.contentWithFooterStyle,
                    gap: 20,
                    paddingTop: 0,
                }}
            >
                <div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 30,
                        }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 28 }}>
                            <Text type="secondary">
                                {`Edit the metadata for your upcoming release. You can view and edit all available app info by visiting ${displayConsoleNameWithPrefix(
                                    appStoreType,
                                )}.`}
                            </Text>
                            <Space size={12}>
                                <Button
                                    type="link"
                                    icon={<RightSquareOutlined />}
                                    target="_blank"
                                    href={metadataURLString}
                                    style={{ padding: 0 }}
                                >
                                    {displayConsoleNameForAppStoreType(appStoreType)} Metadata
                                </Button>
                                <Button
                                    type="link"
                                    icon={<RightSquareOutlined />}
                                    target="_blank"
                                    href={metadataRequirementsLinkForPlatform(platform)}
                                    style={{ padding: 0 }}
                                >
                                    Metadata requirements
                                </Button>
                            </Space>
                        </div>
                        <ReleaseEventsTimelineSection
                            orgId={orgId}
                            appId={appId}
                            maxItemsToDisplay={1}
                            releaseId={releaseId}
                            stepRunId={stepRunComponentProps?.stepRunId}
                            context={ReleaseTimelineSectionContext.ReleaseStep}
                            releaseStepType={releaseStepType}
                        />
                    </div>
                </div>
                {shouldShowTranslationsProviderStatus && (
                    <MetadataTranslationsProviderComponent
                        uploadNeededCount={uploadNeededCount}
                        exportNeededCount={exportNeededCount}
                        autoSyncMetadataTranslationsEnabled={syncMetadataTranslationsAutomation?.enabled ?? false}
                        autoSyncMetadataTranslationsName={syncMetadataTranslationsAutomation?.name ?? ''}
                        appStoreType={appStoreType}
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        integrationId={translationsIntegration.id}
                        integrationName={translationsIntegration?.name}
                        selectedProjectName={selectedMetadataProjectNameForTranslationsIntegration(
                            translationsIntegration,
                        )}
                        selectedProjectURL={selectedMetadataProjectURLForTranslationsIntegration(
                            translationsIntegration,
                        )}
                    />
                )}

                <Tabs destroyInactiveTabPane={false}>
                    {Object.entries<ExtendedFormObject<MetadataFormValues>>(extendedFormObjects).map(
                        ([id, extendedFormObject]) => {
                            const localization = localizations.find((localization) => localization.id === id);
                            const translationsStatus = translationStatusByLocale[localization.locale];

                            const isSourceLocale = localization.locale === translationsSourceLocale;
                            const isPrimaryLocale = localization.locale === primaryLocale;
                            let displayLocale = localization.displayLocale;
                            let ctaButtonDisplayLocale = localization.displayLocale;

                            if (isPrimaryLocale && isSourceLocale) {
                                displayLocale = `${displayLocale} (Primary, Source)`;
                            } else if (isPrimaryLocale) {
                                displayLocale = `${displayLocale} (Primary)`;
                            } else if (isSourceLocale) {
                                displayLocale = `${displayLocale} (Source)`;
                            }

                            if (isSourceLocale) {
                                ctaButtonDisplayLocale = `${ctaButtonDisplayLocale} (source)`;
                            }

                            const isLoading =
                                releaseNotesLoadingById[localization.id] || allReleaseNotesUpdateIsLoading;

                            return (
                                <Tabs.TabPane
                                    forceRender
                                    key={localization.id}
                                    tab={
                                        <Space>
                                            {displayLocale}
                                            {renderLocaleStatusNode(
                                                localization.status,
                                                localization.displayLocale,
                                                appStoreVersionExists,
                                                translationsIntegration?.name,
                                                appStoreType,
                                                localizationsForSourceLocale,
                                                translationsStatus,
                                            )}
                                        </Space>
                                    }
                                    style={{
                                        ...styles.screenshotsLocalizationContentWrapper,
                                        paddingLeft: 24,
                                        paddingRight: 24,
                                    }}
                                >
                                    <StepContentMetadataForm
                                        key={stepRunComponentProps ? stepRunComponentProps.stepRunId : undefined}
                                        appId={appId}
                                        releaseId={releaseId}
                                        orgId={orgId}
                                        appStoreType={appStoreType}
                                        releaseStepType={releaseStepType}
                                        shouldDisableMetadataEditing={shouldDisableMetadataEditing}
                                        ctaButtonDisplayLocale={ctaButtonDisplayLocale}
                                        extendedFormObject={extendedFormObject}
                                        initialValues={localization}
                                        isInvalid={extendedFormStates[localization.id]?.isInvalid}
                                        isDirty={extendedFormStates[localization.id]?.isDirty}
                                        isLoading={isLoading}
                                        isLoadingGetPreviousReleaseNotes={isLoadingGetPreviousReleaseNotesForSpecificId}
                                        defaultReleaseNotesByLocale={defaultReleaseNotesByLocale}
                                        translationStatus={translationsStatus}
                                        isSourceLocale={isSourceLocale}
                                        isTranslationsAutomationEnabled={
                                            syncMetadataTranslationsAutomation?.enabled ?? false
                                        }
                                        translationsIntegrationName={translationsIntegration?.name ?? ''}
                                        shouldShowTranslationsWarningBanner={shouldShowTranslationsProviderStatus}
                                        appStoreVersionExists={appStoreVersionExists}
                                        displayLocale={localization.displayLocale}
                                        locale={localization.locale}
                                        onClickSaveChangesForLocalization={onClickSaveAllLocalizations}
                                        onClickShowFeaturesList={onClickShowFeaturesList}
                                        onClickSetDefaultReleaseNotesForLocale={onClickSetDefaultReleaseNotes}
                                        onClickSetBetaTesterNotesForLocale={onClickSetBetaTesterNotes}
                                        onClickSetPreviousReleaseNotesForLocale={onClickSetPreviousReleaseNotes}
                                        onApplyFieldToAllLocales={onApplyFieldToAllLocales}
                                    />
                                </Tabs.TabPane>
                            );
                        },
                    )}
                </Tabs>

                {localizations.length === 0 && (
                    <Space direction="vertical" align="center" style={{ width: '100%', flexGrow: 1 }}>
                        <Text>No localized metadata found.</Text>
                        <Button onClick={onClickShowFeaturesList}>View feature list</Button>
                    </Space>
                )}
                <CompletedTasksComponent
                    orgId={orgId}
                    appId={appId}
                    releaseId={releaseId}
                    open={featuresListDrawerVisible}
                    onClose={() => setFeaturesListDrawerVisible(false)}
                />
            </Content>
            <Footer style={styles.footerStyle}>
                <Space>
                    {localizations.length > 0 && (
                        <Dropdown
                            disabled={allReleaseNotesUpdateIsLoading || shouldDisableMetadataEditing}
                            menu={{
                                items: dropdownMenuItems,
                            }}
                        >
                            <Button loading={isLoadingGetPreviousReleaseNotes}>
                                <Space>
                                    <Text>Populate release notes for all locales</Text>
                                    <DownOutlined style={{ color: runwayColors.primary }} />
                                </Space>
                            </Button>
                        </Dropdown>
                    )}
                    <div />
                    <PermissionedActionButton
                        releaseId={releaseId}
                        actionType={RunwayUserAction.UAUpdateMetadata}
                        loading={allReleaseNotesUpdateIsLoading}
                        onClick={onClickSaveAllLocalizationsCallback}
                        disabled={
                            anyFormIsInvalid ||
                            areAllFormsPristine ||
                            pendingApproveAfterSave ||
                            anyLocalizationIsLoading ||
                            shouldDisableMetadataEditing
                        }
                        icon={
                            appStoreVersionExists ?
                                <CloudSyncOutlined style={{ color: runwayColors.primary }} />
                            :   <SaveOutlined style={{ color: runwayColors.primary }} />
                        }
                    >
                        {appStoreVersionExists ? 'Save all changes' : 'Save all changes in Runway'}
                    </PermissionedActionButton>
                    {requiresManualApproval ?
                        <ApprovalButtons
                            releaseId={releaseId}
                            isApproved={isApproved}
                            disableApproveButton={disableApproveMetadataButton}
                            approvalIsLoading={approveMetadataIsLoading}
                            updateApprovalOverrideIsLoading={updateApprovalOverrideIsLoading}
                            onClickApprove={onClickApproveMetadataCallback}
                            updateApprovalOverride={updateApprovalOverride}
                            stepLabel="metadata"
                            isWorkflowStep={!!stepRunComponentProps}
                            approveLabel={approveMetadataLabel}
                        />
                    :   <AutoApproveEnabledButton
                            releaseId={releaseId}
                            updateApprovalOverrideIsLoading={updateApprovalOverrideIsLoading}
                            updateApprovalOverride={updateApprovalOverride}
                        />
                    }
                </Space>
            </Footer>
        </Layout>
    );
};
